import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import DatePicker from '@amzn/meridian/date-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import DataTable, {
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableHeader,
} from '@amzn/meridian/data-table';
import { useNavigate } from 'react-router-dom';
import { ROUTES, DEFAULT_SHIFT_OPTIONS } from '../../../utils/constants';
import { buildStationAssignmentPublishedUrl } from '../../../utils/route-helpers';
import {
  getAllShiftPlans,
  getClockWindowNameBasedOnSelectedOption,
} from '../../../services/shiftPlanService';
import { PlanVersion } from '../../../types/shiftPlan';
import { formatTimestamp } from '../../../utils/string-utils';
import { getTodayDate } from '../models';
import { HistoryRow, mapShiftPlansToHistoryRows } from './models';
import './index.scss';

/**
 * Station Assignment History page.
 *
 * Reached from the Station Assignment page's "View history" link. Lets the
 * user pick a Date + Shift and search for previously generated station
 * assignment plans (plan version `LABOR_ALLOCATION_V3`). Each result row can
 * be opened to render the plan on the Published Station Assignment screen.
 *
 * Per product spec this page has NO free-text search, NO pagination and NO
 * results-per-page control — it renders the full result set returned by
 * `getAllShiftPlans`.
 */
const StationAssignmentHistory: React.FC = () => {
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());
  const [selectedShift, setSelectedShift] = useState<string>('day');
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const handleSearch = async () => {
    setIsSearching(true);
    const clockWindowName = getClockWindowNameBasedOnSelectedOption(selectedShift);

    const response = await getAllShiftPlans(
      selectedDate,
      clockWindowName,
      undefined,
      PlanVersion.LABOR_ALLOCATION_V3,
    );

    setRows(mapShiftPlansToHistoryRows(response?.shiftPlans));
    setHasSearched(true);
    setIsSearching(false);
  };

  const handleOpen = (planId: string) => {
    navigate(buildStationAssignmentPublishedUrl(planId));
  };

  return (
    <Box spacingInset="m" height="100%">
      <Heading level={1}>Station assignment history</Heading>
      <Text color="secondary">
        Search previously generated station assignment plans by date and shift.
      </Text>

      {/* Search controls box */}
      <Box className="station-assignment-history-inputs-box" spacingInset="400" type="outline">
        <Row spacing="500" alignmentVertical="bottom" widths="fit">
          <Column spacing="200">
            <Text type="b200" tag="label">Date</Text>
            <DatePicker value={selectedDate} onChange={setSelectedDate} />
          </Column>

          <Column spacing="200">
            <Text type="b200" tag="label">Shift</Text>
            <Select value={selectedShift} onChange={setSelectedShift}>
              {DEFAULT_SHIFT_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </Column>

          <Column spacing="200">
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </Button>
          </Column>
        </Row>
      </Box>

      {/* Results table */}
      <Box spacingInset="400 none">
        <DataTable
          size="large"
          showRowDividers
          showColumnDividers={false}
          showStripes={false}
          showBorder
          width="100%"
        >
          <DataTableHead>
            <DataTableRow>
              <DataTableHeader>Date</DataTableHeader>
              <DataTableHeader>Plan ID</DataTableHeader>
              <DataTableHeader>Created by</DataTableHeader>
              <DataTableHeader>Created at</DataTableHeader>
              <DataTableHeader>Plan</DataTableHeader>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {rows.map((row) => (
              <DataTableRow key={row.planId} highlightOnHover>
                <DataTableCell>
                  <Text>{row.date}</Text>
                </DataTableCell>
                <DataTableCell>
                  <Text>{row.planId}</Text>
                </DataTableCell>
                <DataTableCell>
                  <Text>{row.createdBy}</Text>
                </DataTableCell>
                <DataTableCell>
                  <Text>{formatTimestamp(row.createdAt)}</Text>
                </DataTableCell>
                <DataTableCell>
                  <Link
                    type="secondary"
                    onClick={() => handleOpen(row.planId)}
                    data-testid={`history-open-${row.planId}`}
                  >
                    Open
                  </Link>
                </DataTableCell>
              </DataTableRow>
            ))}
          </DataTableBody>
        </DataTable>

        {hasSearched && !isSearching && rows.length === 0 && (
          <Box spacingInset="400 none">
            <Text color="secondary" alignment="center">
              No station assignment plans found for the selected date and shift.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StationAssignmentHistory;

import './index.scss';